import * as React from 'react';
import { Loader2, Check, Star } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import type { CostCentre } from '@/types/timesheet';

interface CostCentreWithMetadata extends CostCentre {
  lastAccessedDate?: Date;
}

interface CostCentreSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  costCentres: CostCentreWithMetadata[];
  value?: string;
  onSelect: (costCentreId: string) => void;
  onToggleFavourite: (costCentreId: string) => void;
  isFavourite: (costCentreId: string) => boolean;
  /** When false, hides the Favourites section and star toggle buttons (e.g. approver screens) */
  showFavourites?: boolean;
  // Server-side pagination props (optional - for future use)
  onLoadMore?: () => void;
  hasMore?: boolean;
  remainingCount?: number;
  isLoadingMore?: boolean;
  // Server-side search props (optional - for future use)
  onServerSearch?: (query: string) => void;
  isSearching?: boolean;
  totalCount?: number;
  /** When 'all', only cost centres with status active or planning are shown */
  projectSelectionScope?: 'all' | 'assigned';
}

// Status badge styling
function StatusBadge({ status }: { status?: string }) {
  if (!status) return <span className="text-muted-foreground">—</span>;

  const lower = status.toLowerCase();
  const variant =
    lower === 'active'
      ? 'default'
      : lower === 'inactive' || lower === 'closed'
        ? 'destructive'
        : 'secondary';

  return <Badge variant={variant as 'default' | 'secondary' | 'destructive' | 'outline'}>{status}</Badge>;
}

export function CostCentreSearchModal({
  open,
  onOpenChange,
  costCentres,
  value,
  onSelect,
  onToggleFavourite,
  isFavourite,
  showFavourites = true,
  onServerSearch,
  isSearching,
  projectSelectionScope,
}: CostCentreSearchModalProps) {
  const [modalSearchQuery, setModalSearchQuery] = React.useState('');

  React.useEffect(() => {
    if (!onServerSearch) return;
    const timer = setTimeout(() => {
      onServerSearch(modalSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [modalSearchQuery, onServerSearch]);

  React.useEffect(() => {
    if (!open) {
      setModalSearchQuery('');
    }
  }, [open]);

  const handleSelect = React.useCallback(
    (costCentreId: string) => {
      onSelect(costCentreId);
      onOpenChange(false);
    },
    [onSelect, onOpenChange]
  );

  const columns = React.useMemo<ColumnDef<CostCentreWithMetadata>[]>(() => {
    const cols: ColumnDef<CostCentreWithMetadata>[] = [];

    // Selected indicator
    cols.push({
      id: 'selected',
      header: '',
      cell: ({ row }) =>
        value === row.original.id ? (
          <Check className="h-4 w-4 text-primary" />
        ) : null,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      size: 32,
    });

    // Favourite toggle — only when showFavourites is enabled
    if (showFavourites) {
      cols.push({
        id: 'favourite',
        header: '',
        cell: ({ row }) => {
          const isFav = isFavourite(row.original.id);
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavourite(row.original.id);
              }}
              className="flex items-center justify-center"
              aria-label={isFav ? 'Remove from favourites' : 'Add to favourites'}
            >
              <Star
                className={`h-4 w-4 transition-colors ${
                  isFav ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'
                }`}
              />
            </button>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        size: 40,
      });
    }

    // Cost Centre Code
    cols.push({
      accessorKey: 'code',
      header: 'Cost Centre Code',
      size: 140,
    });

    // Cost Centre Name
    cols.push({
      accessorKey: 'name',
      header: 'Cost Centre Name',
      size: 200,
    });

    // Cost Centre Description
    cols.push({
      accessorKey: 'description',
      header: 'Cost Centre Description',
      cell: ({ row }) =>
        row.original.description ? (
          <span>{row.original.description}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      size: 240,
    });

    // Status
    cols.push({
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
      size: 110,
    });

    // User Subtotal
    cols.push({
      accessorKey: 'userSubtotal',
      header: 'User Subtotal',
      cell: ({ row }) =>
        row.original.userSubtotal !== undefined ? (
          <span className="tabular-nums">
            {row.original.userSubtotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      size: 120,
      enableHiding: false,
    });

    return cols;
  }, [value, handleSelect, isFavourite, onToggleFavourite, showFavourites]);

  const fieldLabels: Record<string, string> = {
    selected: '',
    favourite: '',
    code: 'Cost Centre Code',
    name: 'Cost Centre Name',
    description: 'Cost Centre Description',
    status: 'Status',
    userSubtotal: 'User Subtotal',
  };

  const fieldCategories: Record<string, string> = {
    code: 'Cost Centre',
    name: 'Cost Centre',
    description: 'Cost Centre',
    status: 'Cost Centre',
    userSubtotal: 'Financial',
  };

  const initialColumnVisibility: Record<string, boolean> = {
    selected: true,
    ...(showFavourites ? { favourite: true } : {}),
    userSubtotal: false,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[1100px] h-[85vh] max-h-[900px] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            Select Cost Centre
            {isSearching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden px-6 py-4">
          <DataTableEnhanced
            columns={columns}
            data={projectSelectionScope === 'all'
              ? costCentres.filter((cc) => {
                  const s = cc.status?.toLowerCase();
                  return !s || s === 'active' || s === 'planning';
                })
              : costCentres}
            searchKey="name"
            searchPlaceholder="Search by code or name..."
            enableSorting
            enableFiltering
            enableColumnVisibility
            enableGrouping={false}
            enableRowSelection={false}
            pageSize={20}
            storageKey="cost-centre-search-modal"
            fieldLabels={fieldLabels}
            fieldCategories={fieldCategories}
            initialColumnVisibility={initialColumnVisibility}
            showSortIconsWhenNotSorted
            onRowClick={(row) => handleSelect(row.id)}
            onGlobalFilterChange={setModalSearchQuery}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
